<template>
  <div
    class="mc-widget-call-broadcast__wrapper"
    :class="{ 'is-empty-error': isEmptyStateActive || isWsClosed }"
  >
    <div class="mc-widget-call-broadcast__header-wrapper">
      <h3 class="mc-widget-call-broadcast__header-title">
        {{ 'Live Broadcast' }} {{ formattedCount }}
      </h3>
      <div class="mc-widget-call-broadcast__header-action-wrapper">
        <i
          class="mc-fa icon-outline_minus mc-broadcast__header-icon-action"
          :class="{ active: viewState === 'minimize' }"
          @click="viewState = 'minimize'"
        />
        <i
          class="mc-fa icon-outline_square mc-broadcast__header-icon-action"
          :class="{ active: viewState === 'maximize' }"
          @click="viewState = 'maximize'"
        />
        <i
          class="mc-fa icon-fill_expand mc-broadcast__header-icon-action"
          :class="{ active: viewState === 'expanded' }"
          @click="viewState = 'expanded'"
        />
      </div>
    </div>
    <div v-if="viewState === 'expanded'" class="mc-broadcast__info-header-wrapper">
      <div class="mc-broadcast__info-header">
        <span>{{ $t('trackVideo.connected') + ': ' + countBroadcastsState.connected }}</span>
        <span> &bull; </span>
        <span>{{ $t('trackVideo.notConnected') + ': ' + countBroadcastsState.notConnected }}</span>
      </div>
      <span id="sortBroadcast" class="mc-broadcast__btn-sort">
        <i class="mc-fa icon-outline_height" />
      </span>
      <b-popover
        id="sortBroadcastPopover"
        target="sortBroadcast"
        triggers="click blur"
        placement="leftbottom"
        :show.sync="showPopoverSorting"
      >
        <template #title>{{ $t('global.sort') }}</template>
        <div class="mc-broadcast__sort-body-wrapper">
          <p @click="handleClickSort('licensePlate')">
            {{ $t('global.object.licensePlate') }}
            <i v-show="sortActive === 'licensePlate'" class="mc-fa icon-fill_checkmark-circle" />
          </p>
          <p @click="handleClickSort('status')">
            {{ 'Status' }}
            <i v-show="sortActive === 'status'" class="mc-fa icon-fill_checkmark-circle" />
          </p>
        </div>
      </b-popover>
    </div>
    <div
      class="mc-widget-call-broadcast__body-wrapper"
      :class="{
        expanded: viewState === 'expanded',
        minimize: viewState === 'minimize',
        maximize: viewState === 'maximize',
      }"
    >
      <template v-if="!isWsClosed">
        <BroadcastItem
          v-for="imei in Object.keys(calls)"
          :ref="`broadcast-${imei}`"
          :key="imei"
          :licensePlate="calls[imei].licensePlate"
          :driver="calls[imei].driver"
          :state="calls[imei].state"
          :vehicleId="calls[imei].vehicleId"
          :imei="imei"
          :muted="calls[imei].isMuted"
          :audioContext="audioContext"
          @setCallsValue="setCallsValue"
          @removeBroadcastVehicle="removeBroadcastVehicle"
          @reconnectBroadcast="reconnectSingleBroadcast"
        />
      </template>
      <EmptyState
        v-if="isEmptyStateActive"
        @endCall="handleClickEndBroadcast"
        @addVehicle="openModalSelectVehicleBroadcast"
      />
      <ErrorState
        v-else-if="isWsClosed"
        @endCall="handleClickEndBroadcast"
        @reconnect="reconnectBroadcast"
      />
    </div>
    <div v-if="!isEmptyStateActive && !isWsClosed" class="mc-widget-call-broadcast__footer-wrapper">
      <MicSection v-if="isSpeaking" :formattedTime="formattedTime" :audioLevel="audioLevel" />
      <div class="mc-widget-call-broadcast__footer-action-wrapper">
        <b-button variant="outline-primary" @click="openModalSelectVehicleBroadcast">
          <i class="mc-fa icon-fill_plus" />
        </b-button>

        <b-button
          v-if="!isSpeaking"
          variant="primary"
          class="mc-widget-call-broadcast__footer-button-start-speak"
          @click="toggleMuteMicrophone(true)"
        >
          <i class="mc-fa icon-outline_mic" /> {{ $t('trackVideo.broadcast.startSpeak') }}
        </b-button>
        <b-button
          v-else
          variant="primary"
          class="mc-widget-call-broadcast__footer-button-start-speak"
          @click="toggleMuteMicrophone(false)"
        >
          <i class="mc-fa icon-outline_mic-off" /> {{ $t('trackVideo.broadcast.stopSpeak') }}
        </b-button>
        <b-button variant="outline-danger" @click="handleClickEndBroadcast">
          <i class="mc-fa icon-outline_phone-off" />
        </b-button>
      </div>
    </div>
  </div>
</template>
<script>
import { mapGetters } from 'vuex'
import Broadcast from '../lib/broadcast'
import BroadcastItem from './BroadcastItem.vue'
import MicSection from './MicSection.vue'
import EmptyState from './state/EmptyState.vue'
import ErrorState from './state/ErrorState.vue'
import { MixPanel } from '@/mixin/mixpanel'
import { GET_VOIP_TOKEN } from '@/services/api/voip'

export default {
  name: 'WidgetBroadcast',
  components: { BroadcastItem, MicSection, EmptyState, ErrorState },
  mixins: [MixPanel],
  data() {
    return {
      /**
       * hash map with key imei
       */
      calls: {},
      isExpand: false,
      broadcastClass: null,
      audioLevel: 0,
      isSpeaking: false,
      viewState: 'maximize', // minimize | maximize | expanded
      showPopoverSorting: false,
      sortActive: 'licensePlate', //licensePlate | status
      audioContext: null,
      isWsClosed: false,

      timeBroadcastActive: 0, // Initialize time (in milliseconds)
      intervalTimeBroadcastActive: null,
    }
  },

  computed: {
    ...mapGetters({
      getFeatureFlagValue: 'identity/getFeatureFlagValue',
    }),
    ...mapGetters('voip', ['sumBroadcastActive']),
    broadcasts() {
      return this.$store.state.voip.broadcasts || []
    },
    countBroadcastsState() {
      let connected = 0
      let notConnected = 0
      Object.values(this.calls).forEach((call) => {
        if (call.state === 'onGoing') {
          connected++
        } else {
          notConnected++
        }
      })
      return { connected, notConnected }
    },
    isEmptyStateActive() {
      return this.broadcasts.length === 0
    },
    formattedCount() {
      return `(${this.countBroadcastsState.connected}/${this.broadcasts.length})`
    },
    formattedTime() {
      const minutes = String(Math.floor((this.timeBroadcastActive % 3600000) / 60000)).padStart(
        2,
        '0'
      )
      const seconds = String(Math.floor((this.timeBroadcastActive % 60000) / 1000)).padStart(2, '0')
      return `${minutes}:${seconds}`
    },
    broadcastCallbackState() {
      return {
        onopen: () => {},
        onmessage: (evt) => {
          const message = evt.data
          const [_, status, ...restArr] = message.split(':')
          if (status === 'DRS') {
            const imei = restArr[0]
            this.setCallsValue(imei, 'state', 'onGoing')
          } else if (status === 'DRF') {
            const [statusFail, imei] = restArr
            const licensePlate = this.calls[imei]?.licensePlate || ''
            if (statusFail === 'DAOC01') {
              this.setCallsValue(imei, 'state', 'inOtherCall')
              this.$mcToast.error(this.$t('trackVideo.broadcast.vehicleInCall', [licensePlate]))
            } else {
              this.setCallsValue(imei, 'state', 'failConnect')
              this.$mcToast.error(this.$t('trackVideo.broadcast.failConnect', [licensePlate]))
            }
          }
        },
        onerror: (evt) => {},
        onclose: (evt) => {
          this.isWsClosed = true
        },
      }
    },
  },

  watch: {
    sumBroadcastActive(newValue, oldValue) {
      if (newValue === 0 && this.audioContext) {
        this.audioContext.close()
        this.audioContext = null
        this.resetTime()
      } else if (newValue > 0 && oldValue === 0 && !this.audioContext) {
        this.audioContext = new AudioContext()

        // Start the interval to update the time every millisecond
        this.intervalTimeBroadcastActive = setInterval(() => {
          this.timeBroadcastActive += 1000 // Increment time by 1000 milliseconds (1 second)
        }, 1000)
      }
    },
    broadcasts: {
      async handler(newBroadcast, oldBroadcast) {
        const added = newBroadcast.filter(
          (newItem) => !oldBroadcast.some((oldItem) => oldItem.vehicleId === newItem.vehicleId)
        )

        const removed = oldBroadcast.filter(
          (oldItem) => !newBroadcast.some((newItem) => newItem.vehicleId === oldItem.vehicleId)
        )

        if (added.length > 0) {
          this.handleAddedBroadcasts(added)
        }

        if (removed.length > 0) {
          this.handleRemoveBroadcasts(removed)
        }

        await this.$nextTick()

        if (oldBroadcast.length === 0 && newBroadcast.length > 0) {
          this.startBroadcastFromZero()
        }

        if (newBroadcast.length === 0 && oldBroadcast.length > 0) {
          this.stopBroadcast()
        }
      },
      deep: true,
      immediate: false,
    },
  },
  mounted() {},
  beforeDestroy() {
    if (this.audioContext) {
      this.audioContext.close()
    }
    this.resetTime()
    this.stopBroadcast()
  },
  methods: {
    async getTokenVoip(imei) {
      const response = await this.$apollo.query({
        query: GET_VOIP_TOKEN,
        fetchPolicy: 'no-cache',
        variables: {
          filter: {
            imei,
          },
        },
      })
      const result = response.data.getVoipToken
      if (!result?.isSuccessful || !result?.getVoipToken?.token) {
        this.$mcToast.error(this.$t('global.somethingWentWrong'))
      }
      return result.getVoipToken.token
    },
    resetTime() {
      clearInterval(this.intervalTimeBroadcastActive)
      this.timeBroadcastActive = 0
      this.intervalTimeBroadcastActive = null
    },
    openModalSelectVehicleBroadcast() {
      this.$bvModal.show('trackvision-broadcast-modal')
    },

    setCallsValue(imei, key, value) {
      if (!this.calls[imei]) return
      this.calls = {
        ...this.calls,
        [imei]: {
          ...this.calls[imei],
          [key]: value,
        },
      }

      this.$forceUpdate()
    },

    toggleMuteMicrophone(isSpeaking) {
      if (isSpeaking) {
        this.track('Unmute Live Broadcast')
      }
      this.isSpeaking = isSpeaking
      // eslint-disable-next-line no-console
      if (!this.broadcastClass) return console.error('broadcastClass is not initialized')
      this.broadcastClass.toggleMuteMicrophone(!isSpeaking)
    },

    async startBroadcastFromZero() {
      this.isWsClosed = false
      const imeis = Object.keys(this.calls)
      if (imeis.length === 0) return

      const token = await this.getTokenVoip(imeis[0])

      this.broadcastClass = new Broadcast({
        bufferSize: this.getFeatureFlagValue('trackvision_voip_buffer_size'),
        wsDomain: process.env.VUE_APP_VOIP_WS_URL,
        token,
      })

      this.broadcastClass.prepareBroadcast(imeis)
      await this.broadcastClass.startBroadcast({
        callbackState: { ...this.broadcastCallbackState },
      })
      this.broadcastClass.onAudioLevelUpdate = (level) => {
        this.audioLevel = level
      }
    },

    handleAddedBroadcasts(added) {
      added.forEach((vehicle) => {
        this.calls[vehicle.imei] = {
          ...vehicle,
          state: 'connecting',
        }
        this.$forceUpdate()
      })
      // check if broadcast active
      // then push vehicle to message ws
      if (this.broadcastClass) {
        this.broadcastClass.addVehicleBroadcast(added.map((item) => item.imei))
      }
    },
    reconnectBroadcast() {
      this.startBroadcastFromZero()
    },
    reconnectSingleBroadcast(imei) {
      if (!this.broadcastClass) {
        // eslint-disable-next-line no-console
        return console.error('broadcastClass is not initialized')
      }
      this.setCallsValue(imei, 'state', 'connecting')
      this.broadcastClass.reconnectVehicleBroadcast(imei)
    },
    handleRemoveBroadcasts(removed) {
      const calls = { ...this.calls }
      removed.forEach((vehicle) => {
        delete calls[vehicle.imei]
      })
      this.calls = calls
      this.$forceUpdate()
      // check if broadcast active
      // then push vehicle to message ws
      if (this.broadcastClass) {
        this.broadcastClass.removeVehicleBroadcast(removed.map((item) => item.imei))
      }
    },
    removeBroadcastVehicle(vehicleId) {
      this.$store.commit('voip/removeBroadcast', vehicleId)
      this.$forceUpdate()
    },
    handleClickSort(key) {
      this.sortActive = key
      this.showPopoverSorting = false
    },
    handleClickEndBroadcast() {
      Object.keys(this.calls).forEach((imei) => {
        this.removeBroadcastVehicle(this.calls[imei].vehicleId)
      })
      this.$store.commit('voip/setIsBroadcastActive', false)
      this.track('End Live Broadcast')
    },
    stopBroadcast() {
      // eslint-disable-next-line no-console
      if (!this.broadcastClass) console.error('broadcastClass is not initialized')
      this.broadcastClass.stopBroadcast()
    },
  },
}
</script>
<style lang="scss">
.mc-widget-call-broadcast__wrapper {
  width: 376px;
  position: fixed;
  bottom: 32px;
  right: 32px;
  z-index: 1002;
  border-radius: 8px;
  box-shadow: $box-shadow-lg;
  &.is-empty-error {
    .mc-widget-call-broadcast__body-wrapper.maximize {
      height: 300px;
    }
    .mc-widget-call-broadcast__body-wrapper.expanded {
      .mc-widget-call-broadcast__empty-error-state-wrapper {
        margin-top: -32px;
      }
    }
    .mc-widget-call-broadcast__body-wrapper {
      display: flex;
      flex-direction: column;
      justify-content: center;
    }
  }
}

.mc-widget-call-broadcast__header-wrapper {
  padding: 12px;
  background-color: $primary;
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-top-left-radius: 8px;
  border-top-right-radius: 8px;
  h3 {
    color: $white;
    font-weight: 500;
    font-size: 16px;
  }
}

.mc-widget-call-broadcast__header-action-wrapper {
  display: flex;
  align-items: center;
  column-gap: 8px;
  i {
    cursor: pointer;
  }

  .mc-broadcast__header-icon-action.active {
    &::before {
      color: $mep-primary;
    }
    background-color: $white;
    padding: 4px;
    border-radius: 4px;
  }
  i::before {
    color: $white;
    font-size: 18px;
  }
}

.mc-widget-call-broadcast__body-wrapper {
  background-color: $white;
  overflow-y: scroll;
  &.minimize {
    height: 0;
  }
  &.maximize {
    height: 210px;
  }
  &.expanded {
    height: calc(100vh - 250px);
  }
}

.mc-widget-call-broadcast__footer-wrapper {
  border-top: 1px solid $border-color;
  box-shadow: $box-shadow-lg;
  padding: 8px;
  display: flex;
  flex-direction: column;
  row-gap: 12px;
  background-color: $white;
}

.mc-broadcast__info-header-wrapper {
  display: flex;
  justify-content: space-between;
  padding: 8px 16px;
  background-color: $teal-100;
  .mc-broadcast__info-header {
    display: flex;
    column-gap: 8px;
    color: $teal-300;
  }
  .mc-broadcast__btn-sort {
    cursor: pointer;
    .icon-outline_height::before {
      font-size: 16px;
      color: $teal-400;
    }
  }
}

.mc-widget-call-broadcast__footer-action-wrapper {
  display: flex;
  column-gap: 16px;
  justify-content: space-between;
  align-items: center;
  i.mc-fa::before {
    font-size: 18px;
  }
  i.mc-fa {
    margin: 0;
  }
  button {
    display: flex;
    justify-content: center;
    align-items: center;
    column-gap: 8px;
  }
  .mc-widget-call-broadcast__footer-button-start-speak {
    flex: 1;
  }
}

#sortBroadcastPopover {
  padding: 8px;
  border: 1px solid $border-color;
  .popover-header {
    font-weight: 700;
    color: $mep-color-text-400;
    padding: 0;
    border: 0;
    font-size: 13px;
  }
  .mc-broadcast__sort-body-wrapper {
    margin-top: 12px;
    display: flex;
    flex-direction: column;
    row-gap: 4px;
    p {
      transition: background-color 0.1s ease-in-out;
      &:hover {
        background-color: $gray;
      }
      .icon-fill_checkmark-circle::before {
        font-size: 12px;
        color: $green;
      }
      display: flex;
      justify-content: space-between;
      align-items: center;
      column-gap: 8px;
      cursor: pointer;
      padding: 4px 8px;
      margin-bottom: 0px;
    }
  }
}

.mc-widget-call-broadcast__empty-error-state-wrapper {
  padding: 28px 16px;
  img {
    width: 100px;
    margin: 0 auto;
  }
}
.mc-widget-call-broadcast__empty-error-state-img-wrapper {
  display: flex;
  justify-content: center;
  align-items: center;
  margin-bottom: 12px;
}
.mc-widget-call-broadcast__empty-error-state-content {
  text-align: center;
}
.mc-widget-call-broadcast__button-action-wrapper {
  display: flex;
  justify-content: center;
  align-items: center;
  column-gap: 8px;
}
</style>
